/**
 * 全部合集: every collection as a grid of cards, two a row on a phone and
 * three on a wide screen. A card opens its collection.
 */

import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { CoachingNavParams } from '@euler/app/flows/coaching';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { MotiView } from 'moti';
import { ReactNode } from 'react';
import { ScrollView, View } from 'react-native';
import { CollectionCard } from '../../components/CollectionCard';
import { useCoachingCatalogueState } from '../../functions/useCoachingCatalogue';

/** Cards a row on a phone, and on a wide screen. */
const kColumns = 2;
const kWideColumns = 3;
/** Placeholder cards while the catalogue loads. */
const kSkeletons = 6;

export const CoachingCollectionsScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    const columns = useIsDesktopLayout() ? kWideColumns : kColumns;
    const { state, reload } = useCoachingCatalogueState();
    if (state.kind === 'failed')
      return <EmptyView message="合集暂时没加载出来" onRetry={reload} />;
    const cells: ReactNode[] =
      state.kind === 'ready'
        ? state.catalogue.collections.map(collection => (
            <CollectionCard
              key={collection.slug}
              collection={collection}
              onPress={() =>
                navigation.push('collection', { slug: collection.slug })
              }
            />
          ))
        : Array.from({ length: kSkeletons }, (_, index) => (
            <MotiView
              key={index}
              from={{ opacity: 1 }}
              animate={{ opacity: 0.55 }}
              transition={{ type: 'timing', duration: 800, loop: true }}
              style={{
                flex: 1,
                height: rpx(164),
                borderRadius: rpx(14),
                backgroundColor: aiColors.skeleton,
              }}
            />
          ));
    const rows: ReactNode[][] = [];
    for (let i = 0; i < cells.length; i += columns)
      rows.push(cells.slice(i, i + columns));
    return (
      <ScrollView
        contentContainerStyle={{
          width: '100%',
          maxWidth: rpx(768),
          alignSelf: 'center',
          padding: rpx(16),
          paddingBottom: rpx(40),
          gap: rpx(10),
        }}
      >
        {rows.map((row, index) => (
          <View key={index} style={{ flexDirection: 'row', gap: rpx(10) }}>
            {row}
            {/* A short last row keeps its cards the width of the others. */}
            {Array.from({ length: columns - row.length }, (_, pad) => (
              <View key={`pad-${pad}`} style={{ flex: 1 }} />
            ))}
          </View>
        ))}
      </ScrollView>
    );
  },
  { title: '全部合集' },
);
